import React, { createContext, useState, useEffect, useContext } from 'react';
import { signInWithCustomToken } from 'firebase/auth';
import { auth, registerUser, signInUser, signOutUser, onAuthStateChange, registerUserProfile } from '../utils/firebase';
import { setSSOCookie, getSSOCookie, clearSSOCookie, fetchSSOToken } from '../utils/sso';

const noop = async () => {};
// Safe defaults so components using useAuth don't crash when rendered
// outside AuthProvider (tests, isolated component renders).
const DEFAULT_AUTH = { user: null, loading: false, register: noop, login: noop, logout: noop };

export const AuthContext = createContext(DEFAULT_AUTH);
export const useAuth = () => useContext(AuthContext) || DEFAULT_AUTH;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ssoAttempted = false;

    const unsubscribe = onAuthStateChange(async (u) => {
      if (u) {
        setUser(u);
        setLoading(false);
        registerUserProfile(u.uid, u.email).catch(() => {});
        try {
          setSSOCookie(await fetchSSOToken(u));
        } catch (_) {}
      } else if (!ssoAttempted) {
        ssoAttempted = true;
        const ssoToken = getSSOCookie();
        if (ssoToken) {
          try {
            await signInWithCustomToken(auth, ssoToken);
          } catch (_) {
            clearSSOCookie();
            setUser(null);
            setLoading(false);
          }
        } else {
          setUser(null);
          setLoading(false);
        }
      }
    });

    return unsubscribe;
  }, []);

  const register = async (email, password) => {
    const cred = await registerUser(email, password);
    setUser(cred.user);
    return cred.user;
  };

  const login = async (email, password) => {
    const cred = await signInUser(email, password);
    setUser(cred.user);
    return cred.user;
  };

  const logout = async () => {
    clearSSOCookie();
    await signOutUser();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, register, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
